import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';

// I, Q, Z are never issued on UK current-style plates. The composer rejects them
// with a shake animation. See handoff §8.1.
const BANNED_LETTERS = new Set(['I', 'Q', 'Z']);
// Search runs client-side against the already-loaded wordbank — no backend
// call, no minimum slot count required. 0 letters typed = "show the broad
// year inventory"; any concrete letters narrow the match via a regex over
// the cell pattern. Earlier value (2) was a guard against an expensive
// backend search; that search is no longer the path so the guard is gone.
const MIN_LETTERS_FOR_SEARCH = 0;
const TOTAL_SLOTS = 7;
const EMPTY_CHAR = '_';

interface PlateCell {
  index: number;        // 0..6
  value: string;        // letter, digit, or EMPTY_CHAR
  locked: boolean;      // true for the 2 year digits (cells 2 and 3)
  spacer: boolean;      // true for the visual gap-spacer (between letter-4 and the last 3 letters)
}

export interface ByoSubmit {
  pattern: string[];           // 7-length, with EMPTY_CHAR for blanks
  patternDisplay: string;      // e.g. "AB76 C__" with dots/spaces for display
  letterCount: number;         // count of user-supplied letters (excludes locked digits)
}

@Component({
  selector: 'pla-new-release-byo',
  templateUrl: './new-release-byo.component.html',
  styleUrls: ['./new-release-byo.component.scss'],
})
export class NewReleaseByoComponent implements OnChanges, AfterViewInit {
  /** Two-character year string, e.g. "76". Determines locked digits. */
  @Input() year: string = '76';
  /** Surface the composer sits on. 'dark' = the original hero panel (also
   *  what any other embedder gets by default); 'light' = the dev2 Register
   *  hero's paper card. Only the helper-text / button colours change. */
  @Input() surface: 'dark' | 'light' = 'dark';
  @HostBinding('class.on-light') get onLight(): boolean { return this.surface === 'light'; }
  @Output() searchSubmitted = new EventEmitter<ByoSubmit>();

  @ViewChildren('cell') cellEls!: QueryList<ElementRef<HTMLElement>>;

  cells: PlateCell[] = [];
  editableIndices: number[] = [0, 1, 4, 5, 6];
  activeSlot = 0;             // index into editableIndices
  statusText = 'Tap a slot or just type. Empty slots match anything.';
  isReject = false;
  shakeIndex: number | null = null;

  private rejectTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnChanges(): void {
    this.buildCells();
    this.activeSlot = 0;
    this.updateState();
  }

  ngAfterViewInit(): void {
    if (this.cells.length === 0) this.buildCells();
  }

  private buildCells(): void {
    const y = (this.year || '').padEnd(2, ' ').slice(0, 2);
    const next: PlateCell[] = [];
    for (let i = 0; i < TOTAL_SLOTS; i++) {
      if (i === 2 || i === 3) {
        next.push({ index: i, value: y[i - 2], locked: true, spacer: false });
      } else {
        next.push({ index: i, value: EMPTY_CHAR, locked: false, spacer: false });
      }
    }
    this.cells = next;
  }

  // Cells 0,1,2,3, then a 14px spacer, then 4,5,6. The spacer is rendered as a
  // separate empty grid cell rather than via margin (see handoff §8.3).
  cellsBeforeSpacer(): PlateCell[] { return this.cells.slice(0, 4); }
  cellsAfterSpacer(): PlateCell[] { return this.cells.slice(4); }

  onCellClick(cell: PlateCell): void {
    if (cell.locked) return;
    const idx = this.editableIndices.indexOf(cell.index);
    if (idx >= 0) this.setActive(idx);
  }

  setActive(slot: number): void {
    this.activeSlot = slot;
    if (slot < 0 || slot >= this.editableIndices.length) return;
    // Focus the cell element so keyboard nav works for keyboard users.
    const el = this.cellEls?.toArray().find(
      (ref) => ref.nativeElement.getAttribute('data-idx') === String(this.editableIndices[slot]),
    );
    if (el) el.nativeElement.focus();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const tag = (event.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    if (event.key.length === 1 && /[a-zA-Z]/.test(event.key)) {
      const letter = event.key.toUpperCase();
      if (this.activeSlot < 0) {
        const next = this.findNextEmptySlot(0);
        if (next < 0) return;
        this.setActive(next);
      }
      if (BANNED_LETTERS.has(letter)) {
        this.showRejection(this.editableIndices[this.activeSlot]);
        return;
      }
      this.placeLetter(letter);
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      this.handleBackspace();
      return;
    }

    if (event.key === 'ArrowLeft' && this.activeSlot > 0) {
      this.setActive(this.activeSlot - 1);
    } else if (
      event.key === 'ArrowRight' &&
      this.activeSlot >= 0 &&
      this.activeSlot < this.editableIndices.length - 1
    ) {
      this.setActive(this.activeSlot + 1);
    }
  }

  private placeLetter(letter: string): void {
    const cellIdx = this.editableIndices[this.activeSlot];
    this.cells[cellIdx] = { ...this.cells[cellIdx], value: letter };
    const next = this.findNextEmptySlot(this.activeSlot + 1);
    if (next >= 0) {
      this.setActive(next);
    } else {
      this.activeSlot = -1;
    }
    this.updateState();
  }

  private handleBackspace(): void {
    if (this.activeSlot < 0) {
      const lastIdx = this.editableIndices[this.editableIndices.length - 1];
      this.cells[lastIdx] = { ...this.cells[lastIdx], value: EMPTY_CHAR };
      this.setActive(this.editableIndices.length - 1);
      this.updateState();
      return;
    }
    const cellIdx = this.editableIndices[this.activeSlot];
    if (this.cells[cellIdx].value !== EMPTY_CHAR) {
      this.cells[cellIdx] = { ...this.cells[cellIdx], value: EMPTY_CHAR };
    } else if (this.activeSlot > 0) {
      const prevSlot = this.activeSlot - 1;
      const prevIdx = this.editableIndices[prevSlot];
      this.cells[prevIdx] = { ...this.cells[prevIdx], value: EMPTY_CHAR };
      this.setActive(prevSlot);
    }
    this.updateState();
  }

  private findNextEmptySlot(from: number): number {
    for (let i = from; i < this.editableIndices.length; i++) {
      if (this.cells[this.editableIndices[i]].value === EMPTY_CHAR) return i;
    }
    return -1;
  }

  private showRejection(cellIdx: number): void {
    this.shakeIndex = cellIdx;
    this.isReject = true;
    this.statusText = 'I, Q and Z aren\u2019t used on current-style plates';
    if (this.rejectTimer) clearTimeout(this.rejectTimer);
    this.rejectTimer = setTimeout(() => {
      this.isReject = false;
      this.shakeIndex = null;
      this.updateState();
    }, 2200);
  }

  private letterCount(): number {
    return this.cells.filter((c) => !c.locked && c.value !== EMPTY_CHAR).length;
  }

  private updateState(): void {
    if (this.isReject) return;
    const lc = this.letterCount();
    if (lc === 0) {
      this.statusText = 'Tap a slot or just type. Empty slots match anything.';
    } else if (lc < MIN_LETTERS_FOR_SEARCH) {
      const need = MIN_LETTERS_FOR_SEARCH - lc;
      this.statusText = `${need} more letter${need === 1 ? '' : 's'} to enable search`;
    } else {
      const remaining = 5 - lc;
      this.statusText = remaining === 0
        ? 'Exact plate, ready to search'
        : `${remaining} empty slot${remaining === 1 ? '' : 's'} will match any letter`;
    }
  }

  get hasLetters(): boolean { return this.letterCount() > 0; }
  get canSubmit(): boolean { return this.letterCount() >= MIN_LETTERS_FOR_SEARCH; }

  get searchLabel(): string {
    const lc = this.letterCount();
    if (lc === 0) return `Build your ${this.year} plate`;
    if (lc < MIN_LETTERS_FOR_SEARCH) {
      const need = MIN_LETTERS_FOR_SEARCH - lc;
      return `Add ${need} more letter${need === 1 ? '' : 's'}`;
    }
    return 'Search';
  }

  get searchPattern(): string {
    if (!this.canSubmit) return '';
    const display = this.cells.map((c) => {
      if (c.locked) return c.value;
      return c.value === EMPTY_CHAR ? '\u2022' : c.value;
    });
    return `${display.slice(0, 4).join('')} ${display.slice(4).join('')}`;
  }

  clearAll(): void {
    this.cells = this.cells.map((c) =>
      c.locked ? c : { ...c, value: EMPTY_CHAR },
    );
    this.setActive(0);
    this.isReject = false;
    this.shakeIndex = null;
    this.updateState();
  }

  submit(): void {
    if (!this.canSubmit) return;
    const pattern = this.cells.map((c) => c.value);
    this.searchSubmitted.emit({
      pattern,
      patternDisplay: this.searchPattern,
      letterCount: this.letterCount(),
    });
  }
}
